import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { fmtInt, initials, unaccountedOf, type LedgerTotals } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';
import { SEGMENT_COLORS, TRACK_COLOR } from './chartTokens';

const TILES = [
  ['var(--color-dash-lime-soft)', 'var(--color-dash-olive)'],
  ['var(--color-dash-purple-soft)', 'var(--color-dash-purple-ink)'],
  ['var(--color-dash-orange-soft)', 'var(--color-dash-orange)'],
  ['#ddf0ec', 'var(--color-dash-teal)'],
  ['#f8e4f1', '#8a3b7a'],
  ['#efefe8', '#5a5a54'],
] as const;

export function tileOf(i: number): { bg: string; fg: string } {
  const t = TILES[i % TILES.length]!;
  return { bg: t[0], fg: t[1] };
}

/**
 * «Premiums delivered»: un anillo con un solo denominador y, al lado, una
 * fila compacta por artículo que se puede aislar con un clic. Aquí el
 * denominador es lo asignado (o el objetivo de premios entregados del Plan,
 * si lo hay): el modelo no guarda un «esperado» por artículo.
 */
export function PremiumsDelivered({ totals, goal, className }: { totals: LedgerTotals; goal?: number | null; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const [sel, setSel] = useState<number | null>(null);
  const rows = totals.rows;
  const row = sel != null ? rows.find((r) => r.stockId === sel) ?? null : null;
  const base = row ?? totals;
  const delivered = base.delivered;
  const allocated = base.allocated || 1;
  // Denominador (CH-09): el esperado de la calculadora si existe; si no, el
  // objetivo del Plan para el total y lo asignado para un artículo.
  const expected = row ? (row.expected ?? row.allocated) : (totals.expected ?? goal ?? totals.allocated);
  const fromCalc = row ? row.expected != null : totals.expected != null;
  // Hay una referencia distinta de lo asignado: el esperado de la calculadora o la meta del Plan.
  const hasRef = fromCalc || (!row && !!goal);
  const toGoal = expected ? Math.min(140, (delivered / expected) * 100) : 0;
  const headroom = expected ? Math.min(140, (allocated / expected) * 100) : 0;
  const pct = expected ? Math.round(((delivered - expected) / expected) * 100) : null;
  const good = pct != null && pct >= 0;
  const ringColor = good ? SEGMENT_COLORS.delivered : 'var(--color-dash-amber)';
  const dest = [
    { key: 'delivered', v: base.delivered },
    { key: 'pending', v: base.pending },
    { key: 'lapsed', v: base.expired + base.voided },
    { key: 'returned', v: base.returned },
    { key: 'unaccounted', v: unaccountedOf(base) },
  ].filter((d) => d.v > 0) as Array<{ key: keyof typeof SEGMENT_COLORS; v: number }>;
  const light = rows.filter((r) => r.allocated && r.delivered / r.allocated < 0.45);
  const sizeSort = [...rows].sort((a, b) => b.allocated - a.allocated);

  return (
    <section ref={ref} className={cn('flex flex-col gap-[18px] rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-3.5 basis-[300px]">
          <span className="text-[44px] font-bold leading-none tracking-[-0.025em] tabular-nums" style={{ color: good || pct == null ? 'var(--color-dash-olive)' : 'var(--color-dash-orange)' }}>
            {fmtInt(Math.round(totals.delivered * p))}
          </span>
          <div className="flex min-w-0 flex-1 basis-[180px] flex-col gap-[3px]">
            <span className="text-[19px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t('dash.premiums.title')}</span>
            <span className="dash-mono text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>
              {fromCalc && totals.expected != null
                ? t('dash.premiums.headExpected', { expected: fmtInt(totals.expected), allocated: fmtInt(totals.allocated) })
                : goal
                  ? t('dash.premiums.headGoal', { goal: fmtInt(goal), allocated: fmtInt(totals.allocated) })
                  : t('dash.premiums.headAllocated', { allocated: fmtInt(totals.allocated) })}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3.5 text-[12.5px]" style={{ color: 'var(--color-dash-fg-2)' }}>
          <span className="flex items-center gap-[7px]"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: '#c6c6be' }} />{hasRef ? t('dash.premiums.expected') : t('dash.premiums.allocated')}</span>
          <span className="flex items-center gap-[7px]"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: SEGMENT_COLORS.delivered }} />{t('dash.premiums.actual')}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        {/* El anillo */}
        <div className="flex min-w-[196px] basis-[232px] flex-col items-center gap-[13px]">
          <div className="relative h-[186px] w-[186px]">
            <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(var(--color-dash-border) 0 ${((headroom / 1.4) * p).toFixed(1)}%, var(--color-dash-paper-3) 0)` }} />
            <div className="absolute inset-[10px] rounded-full" style={{ background: `conic-gradient(${ringColor} 0 ${((toGoal / 1.4) * p).toFixed(1)}%, transparent 0)` }} />
            <span className="absolute left-1/2 top-0 h-4 w-0.5 -translate-x-1/2 opacity-70" style={{ background: 'var(--color-dash-ink)' }} />
            <div className="absolute inset-9 flex flex-col items-center justify-center gap-[3px] rounded-full" style={{ background: 'var(--color-dash-paper)' }}>
              <span className="dash-mono text-[30px] font-bold leading-none tracking-[-0.02em]" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(Math.round(delivered * p))}</span>
              <span className="px-3.5 text-center text-[11px] leading-[1.3]" style={{ color: 'var(--color-dash-fg-muted)' }}>
                {hasRef ? t('dash.premiums.ofExpected', { n: fmtInt(expected ?? 0) }) : t('dash.premiums.ofAllocated', { n: fmtInt(row ? row.allocated : totals.allocated) })}
              </span>
              <span className="dash-mono rounded-full px-2.5 py-[3px] text-xs font-bold" style={pct == null ? { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-muted)' } : good ? { background: 'var(--color-dash-lime-soft)', color: 'var(--color-dash-olive)' } : { background: 'var(--color-dash-orange-soft)', color: 'var(--color-dash-orange)' }}>
                {pct == null ? '—' : `${pct > 0 ? '+' : ''}${pct}%`}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-[11px]">
            {[
              { label: t('dash.seg.delivered'), value: fmtInt(base.delivered), color: ringColor },
              { label: hasRef ? t('dash.premiums.expected') : t('dash.premiums.allocated'), value: fmtInt(expected ?? 0), color: '#8a8a84' },
              ...(hasRef ? [{ label: t('dash.premiums.allocated'), value: fmtInt(row ? row.allocated : totals.allocated), color: 'var(--color-dash-border)' }] : []),
            ].map((l) => (
              <span key={l.label} className="flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--color-dash-fg-muted)' }}>
                <span className="h-[9px] w-[9px] rounded-[2px]" style={{ background: l.color }} />
                {l.label}
                <span className="dash-mono font-bold" style={{ color: 'var(--color-dash-fg)' }}>{l.value}</span>
              </span>
            ))}
          </div>
          <span className="text-center text-[11px] leading-[1.4] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t('dash.premiums.headroomNote')}</span>
        </div>

        {/* Una fila por artículo */}
        <div className="flex min-w-[250px] flex-1 basis-[280px] flex-col gap-[3px]">
          {sizeSort.map((r, i) => {
            const on = sel === r.stockId;
            const tile = tileOf(i);
            const pc = r.allocated ? Math.round((r.delivered / r.allocated) * 100) : null;
            const ok = pc != null && pc >= 70;
            return (
              <button
                key={r.stockId}
                type="button"
                onClick={() => setSel(on ? null : r.stockId)}
                className="flex items-center gap-2.5 rounded-[9px] border px-2.5 py-[9px] text-left transition-colors hover:bg-[var(--color-dash-paper-2)]"
                style={{ background: on ? 'var(--color-dash-paper-2)' : 'transparent', borderColor: on ? 'var(--color-dash-lime)' : 'var(--color-dash-track)' }}
              >
                {r.image ? (
                  <img src={resolveImageUrl(r.image)} alt="" className="h-7 w-7 shrink-0 rounded-[7px] object-cover" />
                ) : (
                  <span className="dash-mono flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-[10.5px] font-bold" style={{ background: on ? 'var(--color-dash-ink)' : tile.bg, color: on ? 'var(--color-dash-lime)' : tile.fg }}>{initials(r.name)}</span>
                )}
                <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
                  <span className="truncate text-[13.5px] font-semibold" style={{ color: 'var(--color-dash-fg)' }}>{r.name}</span>
                  <span className="relative h-2 overflow-hidden rounded" style={{ background: TRACK_COLOR }}>
                    <span className="absolute inset-y-0 left-0 rounded transition-[width] duration-[650ms]" style={{ width: `${r.allocated ? (r.delivered / r.allocated) * 100 * p : 0}%`, background: pc == null ? 'var(--color-dash-gray)' : ok ? SEGMENT_COLORS.delivered : 'var(--color-dash-amber)' }} />
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-[9px] whitespace-nowrap">
                  <span className="dash-mono min-w-[64px] text-right text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>{fmtInt(r.delivered)} / {fmtInt(r.allocated)}</span>
                  <span className="dash-mono min-w-[54px] rounded-full px-2 py-1 text-center text-xs font-bold" style={pc == null ? { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-subtle)' } : ok ? { background: 'var(--color-dash-lime-soft)', color: 'var(--color-dash-olive)' } : { background: 'var(--color-dash-orange-soft)', color: 'var(--color-dash-orange)' }}>
                    {pc == null ? '—' : `${pc}%`}
                  </span>
                </span>
              </button>
            );
          })}
          <button type="button" onClick={() => setSel(null)} className="mt-[7px] self-start text-xs hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
            {row ? t('dash.premiums.showingItem', { name: row.name }) : t('dash.premiums.clickToIsolate')}
          </button>
        </div>
      </div>

      {row && (
        <div className="flex flex-col gap-2.5 border-t pt-[15px]" style={{ borderColor: 'var(--color-dash-track)' }}>
          <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('dash.premiums.where', { name: row.name })}</span>
          <div className="flex h-[26px] overflow-hidden rounded-[7px]" style={{ background: TRACK_COLOR }}>
            {dest.map((d) => (
              <div key={d.key} title={t(`dash.seg.${d.key}`)} className="transition-[width] duration-[650ms]" style={{ width: `${(d.v / allocated) * 100 * p}%`, background: SEGMENT_COLORS[d.key] }} />
            ))}
          </div>
          <div className="flex flex-wrap gap-3.5">
            {dest.map((d) => (
              <span key={d.key} className="flex items-center gap-[7px] text-xs" style={{ color: 'var(--color-dash-fg-2)' }}>
                <span className="h-[9px] w-[9px] rounded-[2px]" style={{ background: SEGMENT_COLORS[d.key] }} />
                {t(`dash.seg.${d.key}`)}
                <span className="dash-mono font-bold" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(d.v)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="text-[13px] leading-[1.55] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-2)' }}>
        {light.length
          ? t('dash.premiums.lightNote', { n: light.length, total: rows.length, name: light[0]!.name, p: Math.round((light[0]!.delivered / light[0]!.allocated) * 100) })
          : t('dash.premiums.okNote')}
      </p>
    </section>
  );
}
